import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'flowbite-react';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { Dispute, DisputeEvidence } from 'api/escrow/types';
import { useUploadEvidenceMutation } from 'api/mutations/escrow/upload-evidence';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { getDisputeStatusQueryKey } from 'api/queries/escrow/get-dispute-status';
import { DisputeStatusEnum, TERMINAL_DISPUTE_STATUSES } from 'utils/enums';
import FileRoundedIcon from 'assets/svg/FileRounded';

const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// checks whether the evidence file can be previewed in the lightbox (images and PDFs)
function isPreviewable(ev: DisputeEvidence): boolean {
  return ev.fileType.startsWith('image/') || ev.fileType === 'application/pdf';
}

type Props = { dispute: Dispute };

export default function EvidenceSection({ dispute }: Props) {
  const { mutateAsync: uploadEvidence, isPending } = useUploadEvidenceMutation();
  const [description, setDescription] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // previewable evidence: images and PDFs with a file_url
  const previewableEvidence = useMemo(
    () => dispute.evidence.filter((ev) => isPreviewable(ev) && ev.fileUrl),
    [dispute.evidence],
  );

  const isTerminal = TERMINAL_DISPUTE_STATUSES.includes(dispute.status as DisputeStatusEnum);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      enqueueSnackbar('Please select a file', { variant: 'warning' });
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      enqueueSnackbar('File type not allowed. Use JPEG, PNG, PDF, or DOC/DOCX.', { variant: 'warning' });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar('File is too large. Maximum size is 10 MB.', { variant: 'warning' });
      return;
    }

    try {
      await uploadEvidence({
        disputeId: dispute.id,
        file,
        description: description || undefined,
      });

      enqueueSnackbar('Evidence uploaded', { variant: 'success' });
      setDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      void memoryQueryClient.refetchQueries({ queryKey: getDisputeStatusQueryKey(String(dispute.id)) });
    } catch {
      // Error already handled by mutation's onError
    }
  };

  return (
    <div className="bg-brand-purple-card rounded-lg p-4 md:p-6">
      <h3 className="text-lg font-bold text-brand-black mb-4">Evidence</h3>

      {dispute.evidence.length > 0 ? (
        <div className="flex flex-col gap-2 mb-4">
          {dispute.evidence.map((ev) => {
            const clickable = isPreviewable(ev) && ev.fileUrl;
            return (
              <div
                key={ev.id}
                className="flex items-start gap-3 bg-white rounded-lg p-3 border border-brand-purple-border"
              >
                {/* thumbnail or placeholder */}
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && setLightboxIndex(previewableEvidence.indexOf(ev))}
                  className="shrink-0 disabled:cursor-default"
                >
                  {ev.thumbnailUrl ? (
                    <img
                      src={ev.thumbnailUrl}
                      alt={ev.fileName}
                      className="h-16 w-16 rounded object-cover border border-gray-200"
                    />
                  ) : (
                    <FileRoundedIcon size={64} />
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      {clickable ? (
                        <button
                          type="button"
                          onClick={() => setLightboxIndex(previewableEvidence.indexOf(ev))}
                          className="text-sm font-semibold text-blue-600 hover:underline truncate block text-left"
                        >
                          {ev.fileName}
                        </button>
                      ) : (
                        <p className="text-sm font-semibold text-brand-black truncate">{ev.fileName}</p>
                      )}
                      <p className="text-xs text-gray-500">{dayjs(ev.createdAt).format('MMM D, YYYY h:mm A')}</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{(ev.fileSize / 1024).toFixed(0)} KB</span>
                  </div>
                  {ev.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{ev.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-4">No evidence uploaded yet.</p>
      )}

      {/* full-size image lightbox rendered via portal to avoid event propagation issues */}
      {lightboxIndex !== null &&
        previewableEvidence[lightboxIndex] &&
        createPortal(
          <EvidenceLightbox
            items={previewableEvidence}
            index={lightboxIndex}
            onNavigate={setLightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />,
          document.body,
        )}

      {!isTerminal && (
        <div className="flex flex-col gap-3 pt-3 border-t border-gray-200">
          <p className="text-sm font-semibold text-brand-black">Upload Evidence</p>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_MIME_TYPES.join(',')}
            className="text-sm text-brand-black file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-brand-purple-light file:text-brand-black hover:file:bg-brand-purple-border"
          />

          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            maxLength={1000}
            className="py-2 px-4 rounded border border-brand-purple-border text-sm font-semibold"
          />

          <Button
            pill
            color="dark"
            className="font-semibold w-fit"
            onClick={handleUpload}
            isProcessing={isPending}
            disabled={isPending}
          >
            Upload
          </Button>
        </div>
      )}
    </div>
  );
}

// lightbox sub-component with keyboard/button navigation between previewable evidence
function EvidenceLightbox({
  items,
  index,
  onNavigate,
  onClose,
}: {
  items: DisputeEvidence[];
  index: number;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  const evidence = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(index - 1);
  }, [hasPrev, index, onNavigate]);
  const goNext = useCallback(() => {
    if (hasNext) onNavigate(index + 1);
  }, [hasNext, index, onNavigate]);

  // keyboard navigation: arrows/page keys to move, escape to close
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      }
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goPrev, goNext, onClose]);

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/70"
      onMouseDown={onClose}
    >
      {/* close button */}
      <button
        type="button"
        className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300 z-10"
        onClick={onClose}
        aria-label="Close"
      >
        &times;
      </button>

      {/* previous button */}
      {hasPrev && (
        <button
          type="button"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 z-10"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Previous"
        >
          &#8249;
        </button>
      )}

      {/* next button */}
      {hasNext && (
        <button
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 z-10"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Next"
        >
          &#8250;
        </button>
      )}

      {/* content: image or PDF */}
      {evidence.fileType === 'application/pdf' ? (
        <iframe
          src={evidence.fileUrl!}
          title={evidence.fileName}
          className="h-[90vh] w-[90vw] rounded-lg bg-white"
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={evidence.fileUrl!}
          alt={evidence.fileName}
          className="h-[90vh] w-[90vw] object-contain"
          onMouseDown={(e) => e.stopPropagation()}
        />
      )}

      {/* caption: date/time, description, and counter */}
      <div
        className="absolute bottom-4 text-center text-white text-sm max-w-[80vw]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <p className="font-semibold">
          {dayjs(evidence.createdAt).format('MMM D, YYYY h:mm A')}
          {items.length > 1 && (
            <span className="ml-2 text-white/70">
              {index + 1} / {items.length}
            </span>
          )}
        </p>
        {evidence.description && <p className="mt-1 whitespace-pre-line">{evidence.description}</p>}
      </div>
    </div>
  );
}
